import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, Company, Deal, DealList, fmtDay, money } from '../api';
import { useSession } from '../session';
import { ErrorBox, SourceBadge, useAsync } from '../components/ui';
import { Timeline } from '../components/Timeline';
import { AgentPanel } from '../components/AgentPanel';
import { CompanyForm, CompanyInput } from './CompanyForm';
import { ContactForm, ContactInput } from './ContactForm';
import { DealForm, DealInput } from './DealForm';
import { TaskList } from '../components/TaskList';
import { InvoiceCreate } from '../components/InvoiceCreate';

export function CompanyPage() {
  const { id } = useParams();
  const s = useSession();
  const nav = useNavigate();
  const [c, setC] = useState<Company | null>(null);
  const [dups, setDups] = useState<{ id: string; name: string; reason: string }[]>([]);
  const [editing, setEditing] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [addingDeal, setAddingDeal] = useState(false);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [invoicing, setInvoicing] = useState(false);
  const [tick, setTick] = useState(0);
  const load = useAsync();
  const save = useAsync();
  const reload = () => load.run(async () => {
    setC(await api<Company>(`/companies/${id}`));
    setDups((await api<{ items: any[] }>(`/companies/${id}/duplicates`)).items);
    setDeals((await api<DealList>(`/deals?company_id=${id}&limit=100`)).items);
    if (s.role !== 'support') setInvoices((await api<{ items: any[] }>(`/invoices?company_id=${id}&limit=50`).catch(() => ({ items: [] }))).items);
  });
  useEffect(() => { reload(); setEditing(false); }, [id]);

  const update = async (v: CompanyInput) => {
    const r = await save.run(() => api<Company>(`/companies/${id}`, { method: 'PATCH', body: v }));
    if (r) { setEditing(false); reload(); }
  };
  const addContact = async (v: ContactInput) => {
    const r = await save.run(() => api('/contacts', { method: 'POST', body: { ...v, company_id: id } }));
    if (r) { setAddingContact(false); reload(); }
  };
  const addDeal = async (v: DealInput) => {
    const r = await save.run(() => api<Deal>('/deals', { method: 'POST', body: v }));
    if (r) nav(`/ertekesites/${r.id}`);
  };
  const archive = async () => {
    if (!window.confirm('Archiválod a céget? 30 napig visszaállítható.')) return;
    const r = await save.run(() => api(`/companies/${id}/archive`, { method: 'POST' }));
    if (r) nav('/cegek');
  };

  if (load.error) return <ErrorBox error={load.error} />;
  if (!c) return <div className="muted">Betöltés…</div>;
  const fs = c.field_sources;
  const summaryHint = s.on('ai.summary') ? 'Az ügynök is frissítheti; a forrás jelölve lesz.' : undefined;
  return (
    <div className="detail">
      <div className="page-head">
        <h2>{c.name} {c.internal_entity_id && <span className="badge">saját cég</span>}</h2>
        <div className="spacer" />
        {!editing && <button onClick={() => setEditing(true)}>Szerkesztés</button>}
        {s.role === 'owner' && <button className="danger" onClick={archive}>Archiválás</button>}
      </div>
      {dups.length > 0 && (
        <div className="notice">Lehetséges duplikátum: {dups.map((d) => <Link key={d.id} to={`/cegek/${d.id}`}>{d.name} ({d.reason})</Link>)}</div>
      )}
      <div className="cols">
        <div>
          <section className="card">
            {editing ? (
              <CompanyForm initial={c} onSubmit={update} onCancel={() => setEditing(false)} error={save.error} busy={save.busy} summaryHint={summaryHint} />
            ) : (
              <dl className="props">
                <dt>Adószám</dt><dd>{c.tax_number ?? '–'} <SourceBadge src={fs.tax_number} /></dd>
                <dt>Cégjegyzékszám</dt><dd>{c.registration_number ?? '–'}</dd>
                <dt>Székhely</dt><dd>{c.address ? [c.address.zip, c.address.city, c.address.street].filter(Boolean).join(' ') : '–'} <SourceBadge src={fs.address} /></dd>
                <dt>Weboldal</dt><dd>{c.website ? <a href={/^https?:/.test(c.website) ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer">{c.website}</a> : '–'} <SourceBadge src={fs.website} /></dd>
                <dt>Iparág</dt><dd>{c.industry ?? '–'} <SourceBadge src={fs.industry} /></dd>
                <dt>Méret</dt><dd>{c.size ? `${c.size} fő` : '–'}</dd>
                <dt>Címkék</dt><dd>{c.tags.length ? c.tags.map((t) => <span key={t} className="tag">{t}</span>) : '–'}</dd>
                <dt>Összefoglaló</dt><dd className="pre">{c.summary ?? <span className="muted">Még nincs. A Szerkesztésnél kitöltheted.</span>} <SourceBadge src={fs.summary} /></dd>
              </dl>
            )}
          </section>
          <section className="card">
            <div className="card-head"><h3>Kontaktok</h3><button className="secondary" onClick={() => setAddingContact(true)}>Új kontakt</button></div>
            {addingContact && <ContactForm onSubmit={addContact} onCancel={() => setAddingContact(false)} error={save.error} busy={save.busy} hideCompany />}
            <ul className="plain">
              {c.contacts?.map((k: any) => (
                <li key={k.id}><Link to={`/kontaktok/${k.id}`}>{[k.last_name, k.first_name].filter(Boolean).join(' ')}</Link>
                  {k.position && <span className="muted"> · {k.position}</span>}{k.email && <span className="muted"> · {k.email}</span>}</li>
              ))}
              {c.contacts?.length === 0 && <li className="muted">Nincs kontakt.</li>}
            </ul>
          </section>
          <section className="card">
            <div className="card-head"><h3>Dealek</h3>{!addingDeal && <button className="secondary" onClick={() => setAddingDeal(true)}>Új deal</button>}</div>
            {addingDeal && <DealForm fixedCompany={{ id: c.id, name: c.name }} onSubmit={addDeal} onCancel={() => setAddingDeal(false)} error={save.error} busy={save.busy} />}
            <ul className="plain">
              {deals.map((d) => (
                <li key={d.id}><Link to={`/ertekesites/${d.id}`}>{d.title}</Link>
                  <span className="muted"> · {d.stage_name} · {money(d.value, d.currency)}{d.expected_close && ` · zárás: ${fmtDay(d.expected_close)}`}</span>
                  {d.is_stale && <span className="badge badge-risk-external">elakadt</span>}</li>
              ))}
              {deals.length === 0 && <li className="muted">Nincs deal.</li>}
            </ul>
          </section>
          {s.role !== 'support' && (
            <section className="card">
              <div className="card-head"><h3>Számlák</h3>{(s.role === 'owner' || s.role === 'finance') && <button className="secondary" onClick={() => setInvoicing(true)}>Új számla</button>}</div>
              {invoicing && <InvoiceCreate kind="invoice" companyId={c.id} onClose={() => setInvoicing(false)} />}
              <ul className="plain">
                {invoices.map((i) => (
                  <li key={i.id}><Link to={`/szamlak/${i.id}`}>{i.number ?? '(kiállítás alatt)'}</Link> <span className="muted">· {i.kind_hu} · {money(i.gross_total, i.currency)} · {i.status_hu}</span>
                    {i.overdue && <span className="badge badge-risk-external">{i.days_late} napja lejárt</span>}</li>
                ))}
                {invoices.length === 0 && <li className="muted">Nincs számla.</li>}
              </ul>
              {invoices.some((i) => i.open_amount > 0) && <p className="small">Nyitott: <b>{money(invoices.reduce((s2, i) => s2 + (i.currency === 'HUF' ? i.open_amount : 0), 0))}</b></p>}
            </section>
          )}
          <TaskList subject={{ type: 'company', id: c.id }} reloadKey={tick} onChange={() => setTick((x) => x + 1)} />
        </div>
        <div>
          {s.on('ai.agents') && <AgentPanel subject={{ type: 'company', id: c.id }} onChange={() => { reload(); setTick((x) => x + 1); }} />}
          <Timeline path={`/companies/${c.id}`} reloadKey={tick} />
        </div>
      </div>
    </div>
  );
}
